import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Check, X, Lightbulb, Camera, Mic, Users, 
  Volume2, Sun, ArrowLeft, Download, Video
} from 'lucide-react';

export default function RecordingGuide() {
  const dosList = [
    {
      icon: <Sun className="w-5 h-5" />,
      title: "Face the Light Source",
      description: "Position yourself facing a window or light. Natural light on your face creates a professional look."
    },
    {
      icon: <Camera className="w-5 h-5" />,
      title: "Eye Level Camera",
      description: "Hold your phone/camera at eye level. Avoid looking down at the camera—it's unflattering."
    },
    {
      icon: <Mic className="w-5 h-5" />,
      title: "Minimize Background Noise",
      description: "Find a quiet spot. Turn off AC, fans, or noisy equipment before recording."
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Clean Background",
      description: "Choose a simple, uncluttered background. A plain wall or your booth banner works best."
    },
    {
      icon: <Volume2 className="w-5 h-5" />,
      title: "Speak Clearly & Slowly",
      description: "Project your voice. Speak 20% slower than normal—it sounds more professional on camera."
    },
    {
      icon: <Video className="w-5 h-5" />,
      title: "Keep It Concise",
      description: "30 seconds goes fast! Have 2-3 key points ready. Practice once before recording."
    }
  ];

  const dontsList = [
    {
      title: "Don't Stand with Light Behind You",
      description: "Backlight creates a silhouette—your face will be dark and unusable."
    },
    {
      title: "Don't Record in Noisy Areas",
      description: "Trade show floors are loud. Find a quieter corner or use the booth's backdrop."
    },
    {
      title: "Don't Hold the Camera Too Close",
      description: "Keep at least arm's length distance. Too close = distorted face."
    },
    {
      title: "Don't Look at Yourself on Screen",
      description: "Look at the camera lens, not your image. This creates eye contact with viewers."
    },
    {
      title: "Don't Rush",
      description: "Take a breath before speaking. Rushed starts look unprofessional."
    },
    {
      title: "Don't Forget to Smile",
      description: "A genuine smile at the start and end makes your video more engaging."
    }
  ];

  const quickTips = [
    "Test audio by clapping once—if it echoes badly, find a different spot",
    "Wipe your camera lens before recording (fingerprints = blurry video)",
    "Avoid wearing busy patterns or all-white shirts",
    "If outdoors, avoid windy spots—wind noise ruins audio",
    "Have your key message written down nearby as a reminder"
  ];

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 py-4 print:hidden">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
          <button 
            onClick={handlePrint}
            className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-4 py-2 rounded-sm font-semibold transition-colors inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download / Print
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 print:py-4 print:px-8">
        {/* Title */}
        <div className="text-center mb-12 print:mb-6">
          <div className="inline-flex items-center gap-2 bg-[#C41E3A]/20 text-[#C41E3A] px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Video className="w-4 h-4" />
            Recording Guide
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 print:text-3xl">
            Quick Bite Recording Tips
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto print:text-sm">
            Follow these simple dos and don'ts to ensure your 30-second video is professional and usable.
          </p>
        </div>

        {/* Do's Section */}
        <section className="mb-12 print:mb-6">
          <div className="flex items-center gap-3 mb-6 print:mb-3">
            <div className="bg-green-500/20 p-2 rounded-lg">
              <Check className="w-6 h-6 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold print:text-xl">Do's - For Usable Videos</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 print:gap-2">
            {dosList.map((item, index) => (
              <div 
                key={index}
                className="bg-zinc-900 border border-zinc-800 p-5 rounded-lg print:p-3 print:border-gray-300"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-green-500/20 p-2 rounded-full text-green-500 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-bold mb-1 print:text-sm">{item.title}</h3>
                    <p className="text-zinc-400 text-sm print:text-xs">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Don'ts Section */}
        <section className="mb-12 print:mb-6">
          <div className="flex items-center gap-3 mb-6 print:mb-3">
            <div className="bg-red-500/20 p-2 rounded-lg">
              <X className="w-6 h-6 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold print:text-xl">Don'ts - Avoid These Mistakes</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 print:gap-2">
            {dontsList.map((item, index) => (
              <div 
                key={index}
                className="bg-zinc-900 border border-red-900/30 p-5 rounded-lg print:p-3 print:border-gray-300"
              >
                <div className="flex items-start gap-3">
                  <div className="bg-red-500/20 p-2 rounded-full flex-shrink-0">
                    <X className="w-4 h-4 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1 print:text-sm">{item.title}</h3>
                    <p className="text-zinc-400 text-sm print:text-xs">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Tips */}
        <section className="mb-12 print:mb-6">
          <div className="flex items-center gap-3 mb-6 print:mb-3">
            <div className="bg-yellow-500/20 p-2 rounded-lg">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold print:text-xl">Pro Tips</h2>
          </div>
          
          <div className="bg-zinc-900 border border-zinc-800 p-6 rounded-lg print:p-4 print:border-gray-300">
            <ul className="space-y-3">
              {quickTips.map((tip, index) => (
                <li key={index} className="flex items-start gap-3 text-zinc-300 print:text-sm">
                  <Lightbulb className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-[#C41E3A] to-[#8B1428] p-8 rounded-lg print:hidden">
          <h3 className="text-2xl font-bold mb-2">Ready to Record?</h3>
          <p className="text-white/80 mb-6">Put these tips into practice and capture your Quick Bite!</p>
          <Link 
            to="/booth"
            className="bg-white text-[#C41E3A] px-8 py-3 rounded-sm font-bold hover:bg-zinc-100 transition-colors inline-flex items-center gap-2"
          >
            <Video className="w-5 h-5" />
            Start Recording
          </Link>
        </div>

        {/* Print Footer */}
        <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-center text-gray-500 text-xs">
          <p>BrandStage Quick Bite Recording Guide | brandstage-media.com</p>
        </div>
      </main>
    </div>
  );
}
